import { ContainerClient } from "./container/index.js";
import { AgentsClient } from "./agents/index.js";
import { ApprovalClient } from "./approvals/index.js";
import { ProvisionClient } from "./provisions/index.js";
import { OrgClient } from "./org/index.js";
import type { OneCLIOptions } from "./types.js";
import type { RequestOptions } from "./request-options.js";
import type {
  ApplyContainerConfigOptions,
  ContainerConfig,
  GetContainerConfigOptions,
} from "./container/types.js";
import type {
  Agent,
  AgentGrants,
  AgentWithGrantsSummary,
  AppPermissionDefinition,
  ConnectionAgentAccess,
  ConnectionGrantInput,
  ConnectionGrants,
  CreateAgentInput,
  EffectiveAppPermissions,
  CreateAgentResponse,
  EffectiveCredentials,
  EnsureAgentResponse,
} from "./agents/types.js";
import type {
  ManualApprovalCallback,
  ManualApprovalHandle,
} from "./approvals/types.js";
import type {
  ProvisionProjectInput,
  ProvisionProjectResponse,
} from "./provisions/types.js";

const DEFAULT_URL = "https://api.onecli.sh";
const DEFAULT_TIMEOUT = 5000;

export class OneCLI {
  private containerClient: ContainerClient;
  private agentsClient: AgentsClient;
  private approvalClient: ApprovalClient;
  private provisionClient: ProvisionClient;

  /**
   * Organization-scoped resources (connections and rules shared by every
   * project in the org). Requests carry no `X-Project-Id` — authenticate with
   * an organization API key (`oc_org_...`). Requires OneCLI Cloud or a
   * self-hosted Enterprise instance.
   */
  readonly org: OrgClient;

  constructor(options: OneCLIOptions = {}) {
    const apiKey = options.apiKey ?? process.env.ONECLI_API_KEY ?? "";
    const url = options.url ?? process.env.ONECLI_URL ?? DEFAULT_URL;
    const timeout = options.timeout ?? DEFAULT_TIMEOUT;
    const gatewayUrl =
      options.gatewayUrl ?? process.env.ONECLI_GATEWAY_URL ?? null;
    const projectId =
      options.projectId ?? process.env.ONECLI_PROJECT_ID ?? null;

    this.containerClient = new ContainerClient(
      url,
      apiKey,
      timeout,
      projectId,
    );
    this.agentsClient = new AgentsClient(url, apiKey, timeout, projectId);
    this.approvalClient = new ApprovalClient(
      url,
      apiKey,
      gatewayUrl,
      projectId,
    );
    this.provisionClient = new ProvisionClient(
      url,
      apiKey,
      timeout,
      projectId,
    );
    this.org = new OrgClient(url, apiKey, timeout, gatewayUrl);
  }

  /**
   * Fetch the gateway skill markdown from OneCLI.
   */
  getGatewaySkill = (options?: RequestOptions): Promise<string> => {
    return this.containerClient.getGatewaySkill(options);
  };

  /**
   * Fetch the raw container configuration from OneCLI.
   */
  getContainerConfig = (
    options?: GetContainerConfigOptions,
  ): Promise<ContainerConfig> => {
    return this.containerClient.getContainerConfig(options);
  };

  /**
   * Fetch config and apply `-e` / `-v` flags to a Docker `run` argument array.
   * Returns `true` on success, or `false` if OneCLI is unreachable or
   * unhealthy (network error or 5xx). Throws `OneCLIRequestError` on a 4xx
   * response (e.g. unknown agent identifier or invalid API key) -- handle it
   * rather than launching an uncredentialed container.
   */
  applyContainerConfig = (
    args: string[],
    options?: ApplyContainerConfigOptions,
  ): Promise<boolean> => {
    return this.containerClient.applyContainerConfig(args, options);
  };

  /**
   * List all agents in the project.
   */
  listAgents = (options?: RequestOptions): Promise<Agent[]> => {
    return this.agentsClient.listAgents(options);
  };

  /**
   * List all agents with each one's attach summary — its granted connections,
   * secrets, and LLM keys.
   */
  listAgentsWithGrants = (
    options?: RequestOptions,
  ): Promise<AgentWithGrantsSummary[]> => {
    return this.agentsClient.listAgentsWithGrants(options);
  };

  /**
   * The agent's grants: its attached app connections (with per-tool access)
   * and secrets. Grants are attach INTENT — `getEffectiveCredentials` is the
   * effective view with organization guardrails applied.
   */
  getAgentGrants = (
    agentId: string,
    options?: RequestOptions,
  ): Promise<AgentGrants> => {
    return this.agentsClient.getAgentGrants(agentId, options);
  };

  /**
   * Attach an app connection to the agent, or replace its per-tool access.
   * Returns the agent's fresh grant set.
   */
  setConnectionGrant = (
    agentId: string,
    connectionId: string,
    input: ConnectionGrantInput,
    options?: RequestOptions,
  ): Promise<AgentGrants> => {
    return this.agentsClient.setConnectionGrant(
      agentId,
      connectionId,
      input,
      options,
    );
  };

  /**
   * Detach an app connection from the agent (204; resolves to void).
   */
  removeConnectionGrant = (
    agentId: string,
    connectionId: string,
    options?: RequestOptions,
  ): Promise<void> => {
    return this.agentsClient.removeConnectionGrant(
      agentId,
      connectionId,
      options,
    );
  };

  /**
   * Attach a secret or LLM key to the agent (no request body — secrets have
   * no per-tool axis). Returns the agent's fresh grant set.
   */
  attachSecret = (
    agentId: string,
    secretId: string,
    options?: RequestOptions,
  ): Promise<AgentGrants> => {
    return this.agentsClient.attachSecret(agentId, secretId, options);
  };

  /** Detach a secret from the agent (204; resolves to void). */
  detachSecret = (
    agentId: string,
    secretId: string,
    options?: RequestOptions,
  ): Promise<void> => {
    return this.agentsClient.detachSecret(agentId, secretId, options);
  };

  /**
   * Which agents a connection is granted to (attach intent, read-only).
   */
  getConnectionGrants = (
    connectionId: string,
    options?: RequestOptions,
  ): Promise<ConnectionGrants> => {
    return this.agentsClient.getConnectionGrants(connectionId, options);
  };

  /**
   * Create a new agent.
   */
  createAgent = (
    input: CreateAgentInput,
    options?: RequestOptions,
  ): Promise<CreateAgentResponse> => {
    return this.agentsClient.createAgent(input, options);
  };

  /**
   * Ensure an agent exists. Creates it if missing, returns normally if it already exists.
   */
  /**
   * Which credentials this agent can actually use, and what each one can do
   * under the published policy. Read-only.
   *
   * Replaces the retired `GET /v1/agents/:id/{secrets,connections}` reads —
   * those returned a stored assignment list, this returns the EFFECTIVE set.
   */
  getEffectiveCredentials = (
    agentId: string,
    options?: RequestOptions,
  ): Promise<EffectiveCredentials> => {
    return this.agentsClient.getEffectiveCredentials(agentId, options);
  };

  /**
   * What the project's published policy allows for an app, per tool.
   * Replaces the retired `GET /v1/rules/permissions/:provider`.
   */
  getEffectiveAppPermissions = (
    input: { provider: string; agentId?: string },
    options?: RequestOptions,
  ): Promise<EffectiveAppPermissions> => {
    return this.agentsClient.getEffectiveAppPermissions(input, options);
  };

  /**
   * Which agents can reach a connection, and what each can do with it.
   * Replaces the retired `GET /v1/connections/:id/agents`.
   */
  getConnectionAgentAccess = (
    connectionId: string,
    options?: RequestOptions,
  ): Promise<ConnectionAgentAccess> => {
    return this.agentsClient.getConnectionAgentAccess(connectionId, options);
  };

  /**
   * Every provider's public tool catalog — the tool ids an `app`-target policy
   * rule can name.
   */
  listAppPermissionDefinitions = (
    options?: RequestOptions,
  ): Promise<AppPermissionDefinition[]> => {
    return this.agentsClient.listAppPermissionDefinitions(options);
  };

  ensureAgent = (
    input: CreateAgentInput,
    options?: RequestOptions,
  ): Promise<EnsureAgentResponse> => {
    return this.agentsClient.ensureAgent(input, options);
  };

  /**
   * Provision a new project in your organization.
   * Pre-creates a user account, project, and API key.
   * Returns a claim URL and API key. Requires admin/owner role.
   */
  provisionProject = (
    input?: ProvisionProjectInput,
    options?: RequestOptions,
  ): Promise<ProvisionProjectResponse> => {
    return this.provisionClient.provisionProject(input, options);
  };

  /**
   * Register a callback for manual approval requests.
   * Starts background long-polling to the gateway. The callback is called
   * once per pending approval request, concurrently for multiple requests.
   * Returns a handle to stop polling when shutting down.
   */
  configureManualApproval = (
    callback: ManualApprovalCallback,
    options?: RequestOptions,
  ): ManualApprovalHandle => {
    this.approvalClient.start(callback, options).catch(() => {
      // Errors handled internally with backoff
    });
    return { stop: () => this.approvalClient.stop() };
  };
}
